import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { CATEGORIES } from "@/lib/data";
import { ListingCard } from "@/components/ListingCard";
import { Reveal } from "@/components/Reveal";

export default function CatalogPage() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "All";
  const [q, setQ] = useState(params.get("q") || "");
  const [sort, setSort] = useState("featured");
  const [items, setItems] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const query = new URLSearchParams();
      if (category !== "All") query.set("category", category);
      if (q.trim()) query.set("q", q.trim());
      api.get(`/listings?${query.toString()}`).then((r) => setItems(r.data)).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [category, q]);

  const sorted = useMemo(() => {
    if (!items) return null;
    const list = [...items];
    const rate = (l) =>
      l.billing === "once" ? l.flat_rate
        : l.billing === "weekly" ? l.weekly_rate / 7
        : l.billing === "tiered" ? (l.week_prices?.[0] || 0) / 7
        : l.billing === "call" ? Number.MAX_SAFE_INTEGER
        : l.daily_rate;
    if (sort === "price-asc") list.sort((a, b) => rate(a) - rate(b));
    if (sort === "price-desc") list.sort((a, b) => rate(b) - rate(a));
    if (sort === "featured") list.sort((a, b) => Number(b.featured) - Number(a.featured));
    return list;
  }, [items, sort]);

  const pickCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === "All") next.delete("category");
    else next.set("category", c);
    setParams(next);
  };

  return (
    <main className="bg-paper pb-28">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:px-16">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-royal">
            The Catalogue
          </p>
          <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Every tote,{" "}
            <span className="font-serif font-normal italic text-royal">spotless.</span>
          </h1>
          <div className="mt-8 flex max-w-xl items-center gap-2 rounded-2xl border border-slate-200 bg-paper px-4 transition-colors duration-200 focus-within:border-royal">
            <Search className="h-4 w-4 shrink-0 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="catalog-search-input"
              placeholder="Search packages, totes, dollies…"
              className="h-12 w-full bg-transparent text-sm font-medium text-ink outline-none placeholder:text-slate-400"
            />
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {["All", ...CATEGORIES].map((c) => (
              <button
                key={c}
                onClick={() => pickCategory(c)}
                data-testid={`category-chip-${c === "All" ? "all" : c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                  category === c
                    ? "bg-royal text-white shadow-md shadow-royal/25"
                    : "border border-slate-200 bg-white text-slate-600 hover:border-royal/50 hover:text-royal"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-10 sm:px-8 lg:px-16">
        <div className="flex items-center justify-between">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
            {sorted ? `${sorted.length} item${sorted.length === 1 ? "" : "s"}` : "Loading…"}
          </p>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <SlidersHorizontal className="h-4 w-4" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="catalog-sort-select"
              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-ink outline-none transition-colors duration-200 focus:border-royal"
            >
              <option value="featured">Featured first</option>
              <option value="price-asc">Price: low to high</option>
              <option value="price-desc">Price: high to low</option>
            </select>
          </label>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
          {sorted === null &&
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-80 animate-pulse rounded-2xl bg-slate-200/60" />
            ))}
          {sorted?.map((item, i) => (
            <Reveal key={item.id} delay={(i % 3) * 0.08}>
              <ListingCard item={item} />
            </Reveal>
          ))}
          {sorted?.length === 0 && (
            <div className="col-span-full rounded-3xl border border-dashed border-slate-300 bg-white p-16 text-center">
              <p className="font-display text-xl font-bold text-ink">Nothing matches that search.</p>
              <p className="mt-2 text-sm text-slate-500">Try another category or a broader keyword.</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
